import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';

import { ApiService } from '../services/api.service';
import { ToastService } from '../core/services/toast.service';
import { EmployeeFormComponent } from './employee-form/employee-form.component';
import { EmployeeDetailComponent } from './employee-detail/employee-detail.component';
import { EmployeeDocumentsComponent } from './employee-documents/employee-documents.component';
import { extractList } from '../shared/utils/extract-list';


// ==========================================
// EMPLOYEE INTERFACE
// Matches your ASP.NET API model
// ==========================================

interface Employee {

  id: number | string;
  employeeNumber?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  department: string;
  position: string;
  salary: number;
  hireDate: string;
  status: string;
  avatar: string;
  hasProfilePicture?: boolean;
  profilePictureUrl?: string | null;
}


// ==========================================
// COMPONENT
// ==========================================

@Component({

  selector: 'app-employees',

  standalone: true,

  imports: [

    CommonModule,
    FormsModule,

    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTableModule,
    MatMenuModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule

  ],

  templateUrl: './employees.component.html',

  styleUrl: './employees.component.scss'

})


export class EmployeesComponent implements OnInit {


  // ==========================================
  // LOADING
  // ==========================================

  loading = false;


  // ==========================================
  // EMPLOYEES
  // ==========================================

  employees: Employee[] = [];

  filteredEmployees: Employee[] = [];


  // ==========================================
  // SEARCH & FILTER
  // ==========================================

  searchText = '';

  selectedDepartment = 'All';

  selectedStatus = 'All';


  // ==========================================
  // TABLE COLUMNS
  // ==========================================

  displayedColumns: string[] = [

    'employee',

    'employeeId',

    'department',

    'position',

    'contact',

    'status',

    'actions'

  ];


  // ==========================================
  // STATISTICS
  // ==========================================

  totalEmployees = 0;

  activeEmployees = 0;

  inactiveEmployees = 0;

  newEmployees = 0;


  // ==========================================
  // FILTER OPTIONS
  // ==========================================

  departments: string[] = [

    'All'

  ];


  statuses: string[] = [

    'All',

    'Active',

    'Inactive'

  ];


  // ==========================================
  // CONSTRUCTOR
  // ==========================================

  constructor(

    private apiService: ApiService,

    private dialog: MatDialog,

    private router: Router,

    private toast: ToastService

  ) {}


  // ==========================================
  // INITIALIZE
  // ==========================================

  ngOnInit(): void {

    this.loadEmployees();

    // Quick-create deep link: /employees/new
    if (this.router.url.split('?')[0].endsWith('/employees/new')) {
      queueMicrotask(() => {
        this.addEmployee();
        void this.router.navigateByUrl('/employees', { replaceUrl: true });
      });
    }

  }


  // ==========================================
  // LOAD EMPLOYEES
  // GET /api/EmployeeForm
  // ==========================================

  loadEmployees(): void {

    this.loading = true;


    this.apiService
      .getEmployees()
      .subscribe({

        next: (response) => {

          console.log(
            'Employees Page API Response:',
            response
          );


          const employeeData = extractList(response);

          this.employees = employeeData.map((employee: any) => {
            const firstName = employee.firstName || '';
            const lastName = employee.lastName || '';
            const fullName =
              employee.displayName ||
              `${firstName} ${employee.middleName || ''} ${lastName}`.replace(/\s+/g, ' ').trim() ||
              'Unknown Employee';

            const statusRaw = employee.status;
            const statusLabel = typeof statusRaw === 'number'
              ? ['Active', 'On leave', 'Probation', 'Terminated', 'Resigned'][statusRaw] || String(statusRaw)
              : (statusRaw || 'Active');

            return {
              id: employee.id || 0,
              employeeNumber: employee.employeeNumber || '',
              firstName,
              middleName: employee.middleName || '',
              lastName,
              email: employee.companyEmail || employee.personalEmail || 'N/A',
              phone: employee.mobileNumber || 'N/A',
              department: employee.departmentName || 'N/A',
              position: employee.jobTitle || 'N/A',
              salary: 0,
              hireDate: employee.hireDate || '',
              status: statusLabel,
              avatar: this.getInitials(fullName),
              hasProfilePicture: !!employee.hasProfilePicture,
              profilePictureUrl: employee.profilePictureUrl || null
            };
          });


          // ==========================================
          // INITIAL FILTERED DATA
          // ==========================================

          this.filteredEmployees = [

            ...this.employees

          ];


          // ==========================================
          // CALCULATE STATISTICS
          // ==========================================

          this.calculateStatistics();


          // ==========================================
          // LOAD DEPARTMENTS
          // ==========================================

          this.loadDepartments();


          this.loading = false;

        },


        error: (error) => {

          console.error(

            'Employees API Error:',

            error

          );


          this.loading = false;

        }

      });

  }


  // ==========================================
  // CALCULATE STATISTICS
  // ==========================================

  calculateStatistics(): void {


    // Total

    this.totalEmployees =

      this.employees.length;


    // Active

    this.activeEmployees =

      this.employees.filter(

        employee =>

          employee.status

            ?.toLowerCase() === 'active'

      ).length;


    // Inactive

    this.inactiveEmployees =

      this.employees.filter(

        employee =>

          employee.status

            ?.toLowerCase() === 'inactive'

      ).length;


    // Temporary
    // Later calculate using hireDate

    this.newEmployees =

      this.employees.filter(

        employee => {

          if (!employee.hireDate) {

            return false;

          }


          const hireDate =

            new Date(
              employee.hireDate
            );


          const currentDate =

            new Date();


          const difference =

            currentDate.getTime() -

            hireDate.getTime();


          const days =

            difference /

            (1000 * 60 * 60 * 24);


          return days <= 30;

        }

      ).length;

  }


  // ==========================================
  // LOAD DEPARTMENTS
  // ==========================================

  loadDepartments(): void {


    const departmentList =

      this.employees

        .map(

          employee =>

            employee.department

        )

        .filter(

          department =>

            department &&

            department !== 'N/A'

        );


    this.departments = [

      'All',

      ...Array.from(

        new Set(

          departmentList

        )

      )

    ];

  }


  // ==========================================
  // FILTER EMPLOYEES
  // ==========================================

  filterEmployees(): void {


    const search =

      this.searchText

        .toLowerCase()

        .trim();


    this.filteredEmployees =

      this.employees.filter(

        employee => {


          // ==========================================
          // FULL NAME
          // ==========================================

          const fullName =

            `${employee.firstName} ${employee.lastName}`

              .toLowerCase();


          // ==========================================
          // SEARCH
          // ==========================================

          const matchesSearch =

            fullName.includes(

              search

            )

            ||

            employee.email

              .toLowerCase()

              .includes(

                search

              )

            ||

            employee.phone

              .toLowerCase()

              .includes(

                search

              )

            ||

            employee.department

              .toLowerCase()

              .includes(

                search

              )

            ||

            employee.position

              .toLowerCase()

              .includes(

                search

              );


          // ==========================================
          // DEPARTMENT FILTER
          // ==========================================

          const matchesDepartment =

            this.selectedDepartment === 'All'

            ||

            employee.department ===

              this.selectedDepartment;


          // ==========================================
          // STATUS FILTER
          // ==========================================

          const matchesStatus =

            this.selectedStatus === 'All'

            ||

            employee.status ===

              this.selectedStatus;


          // ==========================================
          // FINAL RESULT
          // ==========================================

          return (

            matchesSearch &&

            matchesDepartment &&

            matchesStatus

          );

        }

      );

  }


  // ==========================================
  // CLEAR FILTERS
  // ==========================================

  clearFilters(): void {


    this.searchText = '';


    this.selectedDepartment =

      'All';


    this.selectedStatus =

      'All';


    this.filteredEmployees = [

      ...this.employees

    ];

  }

  exportEmployeesCsv(): void {
    const dept = this.selectedDepartment;
    this.apiService.exportEmployeesCsv(dept === 'All' ? undefined : dept).subscribe({
      next: (blob) => {
        this.downloadBlob(blob, `employees_${(dept || 'all').replace(/\s+/g, '_')}.csv`);
        this.toast.success(
          dept && dept !== 'All'
            ? `Exported employees for ${dept}.`
            : 'Exported all employees.'
        );
      },
      error: (err) => this.toast.error(err?.message || 'Failed to export employees CSV.')
    });
  }

  downloadEmployeeTemplate(): void {
    const csv = [
      'EmployeeNumber,FirstName,LastName,Email,JobTitle,Department,HireDate,EmploymentType,Status,MobileNumber,WorkLocation',
      'EMP-100,Jane,Doe,jane.doe@example.com,Developer,Engineering,2026-01-15,FullTime,Active,+10000000000,HQ'
    ].join('\n');
    this.downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'employees_import_template.csv');
    this.toast.info('Downloaded employee CSV template.');
  }

  onEmployeeCsvPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    const dept = this.selectedDepartment === 'All' ? undefined : this.selectedDepartment;
    this.apiService.importEmployeesCsv(file, dept).subscribe({
      next: (res) => {
        const data = res?.data ?? res;
        const created = data?.created ?? 0;
        const updated = data?.updated ?? 0;
        const failed = data?.failed ?? 0;
        this.toast.success(
          (res?.message || `Imported ${created + updated} employee row(s).`) +
            (failed ? ` ${failed} row(s) skipped.` : '') +
            (dept ? ` Department forced: ${dept}.` : '')
        );
        this.loadEmployees();
      },
      error: (err) => this.toast.error(err?.error?.message || err?.message || 'Failed to import employees CSV.')
    });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }


  // ==========================================
  // ADD EMPLOYEE
  // ==========================================

  addEmployee(): void {

    const dialogRef = this.dialog.open(EmployeeFormComponent, {

      width: '960px',
      maxWidth: '96vw',

      data: {}

    });

    dialogRef.afterClosed().subscribe((result) => {

      if (result) {
        this.loadEmployees();
      }

    });
  }


  // ==========================================
  // VIEW EMPLOYEE
  // GET /api/EmployeeForm/{id}
  // ==========================================

  viewEmployee(

    employee: Employee

  ): void {

    this.dialog.open(EmployeeDetailComponent, {

      width: '780px',
      maxWidth: '96vw',

      data: { id: employee.id }

    });
  }

  openDocuments(employee: Employee): void {
    if (!employee.id) return;
    this.dialog.open(EmployeeDocumentsComponent, {
      width: '1000px',
      maxWidth: '96vw',
      data: {
        employeeId: String(employee.id),
        employeeName: `${employee.firstName} ${employee.lastName}`.trim()
      }
    });
  }


  // ==========================================
  // EDIT EMPLOYEE
  // ==========================================

  editEmployee(

    employee: Employee

  ): void {

    if (!employee.id) {

      return;

    }


    const dialogRef = this.dialog.open(EmployeeFormComponent, {

      width: '960px',
      maxWidth: '96vw',

      data: { ...employee }

    });


    dialogRef.afterClosed().subscribe((result) => {

      if (result) {
        this.loadEmployees();
      }

    });
  }


  // ==========================================
  // DELETE EMPLOYEE
  // ==========================================

  deleteEmployee(

    employee: Employee

  ): void {


    if (

      !employee.id

    ) {


      console.error(

        'Employee ID is missing'

      );


      return;

    }


    const fullName =

      `${employee.firstName} ${employee.lastName}`.trim();


    const confirmed =

      confirm(

        `Are you sure you want to delete ${fullName}?`

      );


    if (!confirmed) {

      return;

    }


    this.apiService

      .deleteEmployee(

        employee.id

      )

      .subscribe({

        next: () => {


          console.log(

            'Employee deleted successfully'

          );


          // Reload employees

          this.loadEmployees();

        },


        error: (error) => {


          console.error(

            'Delete Employee Error:',

            error

          );


        }

      });

  }


  // ==========================================
  // GET INITIALS
  // ==========================================

  getInitials(name: string): string {
    if (!name) {
      return 'NA';
    }

    return name
      .split(' ')
      .filter((part) => part.length > 0)
      .map((part) => part.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

}